import React, { useState, useMemo, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Drawer from "@material-ui/core/Drawer";
import Link from "@material-ui/core/Link";
import InputLabel from "@material-ui/core/InputLabel";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import Tooltip from "@material-ui/core/Tooltip";
import Box from "@material-ui/core/Box";
import { i18n } from "../../translate/i18n";

import ContactModal from "../ContactModal";
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import MarkdownWrapper from "../MarkdownWrapper";
import FileCopyIcon from '@material-ui/icons/FileCopy';
import LanguageIcon from '@material-ui/icons/Language';
import { AuthContext } from "../../context/Auth/AuthContext";
import { sanitizeContact } from "../../helpers/sanitizeContact";

const drawerWidth = 320;

const useStyles = makeStyles(theme => ({
	drawer: {
		width: drawerWidth,
		flexShrink: 0,
	},
	drawerPaper: {
		width: drawerWidth,
		display: "flex",
		borderTop: "1px solid rgba(0, 0, 0, 0.12)",
		borderRight: "1px solid rgba(0, 0, 0, 0.12)",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		borderTopRightRadius: 4,
		borderBottomRightRadius: 4,
	},
	header: {
		display: "flex",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		backgroundColor:
      		theme.palette.type === "dark" ? theme.palette.background.default : "#eee",
		alignItems: "center",
		padding: theme.spacing(0, 1),
		minHeight: "73px",
		justifyContent: "flex-start",
	},
	content: {
		display: "flex",
		backgroundColor:
      		theme.palette.type === "dark" ? theme.palette.background.default : "#eee",
		flexDirection: "column",
		padding: "8px 0px 8px 8px",
		height: "100%",
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},
	contactAvatar: {
		margin: 15,
		width: 160,
		height: 160,
	},
	contactHeader: {
		display: "flex",
		padding: 8,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		"& > *": {
			margin: 4,
		},
	},
	contactDetails: {
		marginTop: 8,
		padding: 8,
		display: "flex",
		flexDirection: "column",
	},
	contactExtraInfo: {
		marginTop: 4,
		padding: 6,
	},
  linkInfo: {
    color: theme.palette.type === "dark" ? "#4fc3f7" : "#1976d2",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
    display: 'inline-block',
    maxWidth: '180px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    verticalAlign: 'middle',
  },
}));

const ContactDrawer = ({ open, handleDrawerClose, contact, loading }) => {
	const classes = useStyles();
	const [modalOpen, setModalOpen] = useState(false);
  const { user } = useContext(AuthContext);
  
  const handleCopy = (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text);
    } else {
      const tempInput = document.createElement('textarea');
      tempInput.value = text;
      document.body.appendChild(tempInput);
      tempInput.select();
      document.execCommand('copy');
      document.body.removeChild(tempInput);
    }
  };

  const displayContact = sanitizeContact(contact, user);

  const { linkInfo, otherInfo } = useMemo(() => {
		if (!displayContact?.extraInfo) {
			return { linkInfo: null, otherInfo: [] };
		}
		const link = displayContact.extraInfo.find(info => info.name === 'Link');
		const others = displayContact.extraInfo.filter(info => info.name !== 'Link');
		return { linkInfo: link, otherInfo: others };
	}, [displayContact]);

  const canViewNumber = contact && displayContact.number === contact.number;
  
	return (
		<Drawer
			className={classes.drawer}
			variant="persistent"
			anchor="right"
			open={open}
			PaperProps={{ style: { position: "absolute" } }}
			BackdropProps={{ style: { position: "absolute" } }}
			ModalProps={{
				container: document.getElementById("drawer-container"),
				style: { position: "absolute" },
			}}
			classes={{
				paper: classes.drawerPaper,
			}}
		>
			<div className={classes.header}>
				<IconButton onClick={handleDrawerClose}>
					<CloseIcon />
				</IconButton>
				<Typography style={{ justifySelf: "center" }}>
					{i18n.t("contactDrawer.header")}
				</Typography>
			</div>
			{loading || !displayContact ? (
				<ContactDrawerSkeleton classes={classes} />
			) : (
				<div className={classes.content}>
					<Paper square variant="outlined" className={classes.contactHeader}>
						<Avatar
							alt={displayContact.name}
							src={displayContact.profilePicUrl}
							className={classes.contactAvatar}
						/>
						<Typography>{displayContact.name}</Typography>
						<Box display="flex" alignItems="center">
              <Typography>
                <Link href={canViewNumber ? `tel:${displayContact.number}` : undefined} color="inherit">
                  {displayContact.number}
                </Link>
              </Typography>
              {canViewNumber && (
                <Tooltip title="Copiar número" placement="right">
                  <IconButton onClick={() => handleCopy(displayContact.number)} style={{ padding: 4 }}>
                    <FileCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            <Box display="flex" alignItems="center">
              <Typography style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {displayContact.email ? (
                  <Link href={`mailto:${displayContact.email}`}>{displayContact.email}</Link>
                ) : ("")}
              </Typography>
              {displayContact.email && (
                <Tooltip title="Copiar correo" placement="right">
                  <IconButton onClick={() => handleCopy(displayContact.email)} style={{ padding: 4 }}>
                    <FileCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {linkInfo && (
                <Box display="flex" alignItems="center">
                  <LanguageIcon fontSize="small" style={{ marginRight: 4, color: 'grey' }} />
                  <Typography>
                    <a
                      href={linkInfo.value}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.linkInfo}
                    >
                      {linkInfo.value}
                    </a>
                  </Typography>
                  <Tooltip title="Copiar link" placement="right">
                    <IconButton onClick={() => handleCopy(linkInfo.value)} style={{ padding: 4 }}>
                      <FileCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
						<Button
							variant="outlined"
							color="primary"
							onClick={() => setModalOpen(true)}
						>
							{i18n.t("contactDrawer.buttons.edit")}
						</Button>
					</Paper>
          {contact && (
            <ContactModal
              open={modalOpen}
              onClose={() => setModalOpen(false)}
              contactId={contact.id}
              key={contact.id}
            />
          )}
          {otherInfo.length > 0 && (
            <Paper square variant="outlined" className={classes.contactDetails}>
              <Typography variant="subtitle1">
                {i18n.t("contactDrawer.extraInfo")}
              </Typography>
              {otherInfo.map(info => {
                  const cleanValue = info.value.replace(/\\/g, "");
                  const isLink = /^https?:\/\//i.test(cleanValue);

                return (
                  <Paper
                    key={info.id}
                    square
                    variant="outlined"
                    className={classes.contactExtraInfo}
                  >
                    <Typography component="div" noWrap style={{ paddingTop: 2 }}>
                      {isLink ? (
                        <a
                          href={cleanValue}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={classes.linkInfo}
                        >
                          {info.name}
                        </a>
                      ) : (
                        <>
                          <InputLabel>{info.name}</InputLabel>
                          <MarkdownWrapper>{info.value}</MarkdownWrapper>
                        </>
                      )}
                    </Typography>
                  </Paper>
                );
              })}
            </Paper>
          )}
				</div>
			)}
		</Drawer>
	);
};

export default ContactDrawer;